import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import Register from './pages/Register';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminPlans from './pages/admin/Plans';
import AdminSubscriptions from './pages/admin/Subscriptions';
import AdminSettings from './pages/admin/Settings';
import AdminCredentials from './pages/admin/Credentials';
import TeacherDashboard from './pages/teacher/Dashboard';
import TeacherExams from './pages/teacher/Exams';
import ExamBuilder from './pages/teacher/ExamBuilder';
import TeacherResults from './pages/teacher/Results';
import Subscribe from './pages/teacher/Subscribe';
import TeacherProfile from './pages/teacher/Profile';
import TeacherBranding from './pages/teacher/Branding';
import StudentExam from './pages/student/Exam';
import StudentResult from './pages/student/Result';
import Leaderboard from './pages/student/Leaderboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import Landing from './pages/Landing';

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#0f0f1a', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
      <div style={{ width:40, height:40, border:'3px solid #2e2e50', borderTopColor:'#6366f1', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" />;
  return children;
}

function DashboardRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'admin') return <Navigate to="/admin" />;
  return <Navigate to="/teacher" />;
}

function AnimatedRoutes() {
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top:0, behavior:'instant' }); }, [location.pathname]);
  return (
    <div key={location.pathname} style={{ animation:'pageFade 0.12s ease-in' }}>
      <style>{`@keyframes pageFade{from{opacity:0}to{opacity:1}}`}</style>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/register" element={<Register />} />
        <Route path="/exam/:token" element={<StudentExam />} />
        <Route path="/result/:attemptId" element={<StudentResult />} />
        <Route path="/leaderboard/:token" element={<Leaderboard />} />
        <Route path="/dashboard" element={<PrivateRoute><DashboardRedirect /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute roles={['admin']}><AdminDashboard /></PrivateRoute>} />
        <Route path="/admin/users" element={<PrivateRoute roles={['admin']}><AdminUsers /></PrivateRoute>} />
        <Route path="/admin/plans" element={<PrivateRoute roles={['admin']}><AdminPlans /></PrivateRoute>} />
        <Route path="/admin/subscriptions" element={<PrivateRoute roles={['admin']}><AdminSubscriptions /></PrivateRoute>} />
        <Route path="/admin/settings" element={<PrivateRoute roles={['admin']}><AdminSettings /></PrivateRoute>} />
        <Route path="/admin/credentials" element={<PrivateRoute roles={['admin']}><AdminCredentials /></PrivateRoute>} />
        <Route path="/teacher" element={<PrivateRoute roles={['science_teacher','arts_teacher','school']}><TeacherDashboard /></PrivateRoute>} />
        <Route path="/teacher/exams" element={<PrivateRoute roles={['science_teacher','arts_teacher','school']}><TeacherExams /></PrivateRoute>} />
        <Route path="/teacher/exams/new" element={<PrivateRoute roles={['science_teacher','arts_teacher','school']}><ExamBuilder /></PrivateRoute>} />
        <Route path="/teacher/exams/:id/edit" element={<PrivateRoute roles={['science_teacher','arts_teacher','school']}><ExamBuilder /></PrivateRoute>} />
        <Route path="/teacher/exams/:id/results" element={<PrivateRoute roles={['science_teacher','arts_teacher','school']}><TeacherResults /></PrivateRoute>} />
        <Route path="/teacher/subscribe" element={<PrivateRoute roles={['science_teacher','arts_teacher','school','coaching']}><Subscribe /></PrivateRoute>} />
        <Route path="/teacher/profile" element={<PrivateRoute roles={['science_teacher','arts_teacher','school']}><TeacherProfile /></PrivateRoute>} />
        <Route path="/teacher/branding" element={<PrivateRoute roles={['science_teacher','arts_teacher','school']}><TeacherBranding /></PrivateRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/" element={<Landing />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <HashRouter>
        <Toaster position="top-right" toastOptions={{ style:{ background:'#1e1e35', color:'#e2e8f0', border:'1px solid #2e2e50', fontFamily:'Sora, sans-serif' } }} />
        <AnimatedRoutes />
      </HashRouter>
    </AuthProvider>
  );
}
